'use client';
import {
  Send,
  MessageCircle,
  Users,
  X,
  // Sun,
  // Moon,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { LogoIcon, LogoTextIcon } from '@/components/icons';
import { PinterestIcon } from '@/components/icons/pinterest';
import { socialLinks } from '@/data/social-links';
import { createCatalogNavigateHandler } from '@/features/fanfic-card/consts/LISTING_FANDOM_NAVIGATE_EVENT';

interface MobileMenuProps {
  isOpen: boolean;
  onClose: () => void;
  // isDark: boolean;
  // toggleTheme: () => void;
  // mounted: boolean;
  isAuthenticated: boolean;
}

export function MobileMenu({
  isOpen,
  onClose,
  // isDark,
  // toggleTheme,
  // mounted,
  isAuthenticated,
}: MobileMenuProps) {
  const pathname = usePathname();

  const isActiveLink = (href: string) => {
    if (href === '/media') {
      return pathname === '/media';
    }
    if (href === '/wiki') {
      return pathname === '/wiki' || pathname.startsWith('/wiki/');
    }
    if (href === '/author-dashboard') {
      return pathname.startsWith('/author-dashboard');
    }
    return pathname.startsWith(href);
  };

  const getLinkClassName = (href: string) => {
    const baseClass = 'text-mobile-nav font-mobile-nav transition-colors text-right';
    const isActive = isActiveLink(href);

    if (isActive) {
      return `${baseClass} text-white border-b-2 border-[#FF3DAE] pb-2`;
    }

    return `${baseClass} text-white hover:text-[#898989]`;
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 bg-[#1B1D22] z-50 lg:hidden h-[100dvh] overflow-y-hidden overscroll-contain"
          style={{ height: '100dvh' }}
          data-mobile-menu
        >
          <div className="flex items-center justify-between px-4 py-4">
            <AnimatePresence>
              <motion.div
                className="flex items-center space-x-2 max-sm:space-x-0"
                initial={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.2 }}
              >
                <LogoIcon className="max-sm:w-6.5 max-sm:h-7" />
                <LogoTextIcon className="text-2xl font-bold text-white pb-1 max-sm:w-[75px] max-sm:h-7" />
              </motion.div>
            </AnimatePresence>

            <button
              onClick={onClose}
              className="w-10 h-10 rounded-[10px] bg-[#3a3f47] flex items-center justify-center"
            >
              <X className="w-5 h-5 text-[#898989]" />
            </button>
          </div>

          <motion.div
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.3 }}
            className="flex flex-col justify-center items-end px-8 h-full pb-32"
          >
            <div className="flex flex-col items-end space-y-8 mb-16">
              <Link
                href="/fanfics"
                className={getLinkClassName('/fanfics')}
                onClick={onClose}
                onNavigate={createCatalogNavigateHandler('/fanfics')}
              >
                Фанфики
              </Link>
              <Link
                href="/originals"
                className={getLinkClassName('/originals')}
                onClick={onClose}
                onNavigate={createCatalogNavigateHandler('/originals')}
              >
                Ориджиналы
              </Link>
              <Link href="/media" className={getLinkClassName('/media')} onClick={onClose}>
                Медиа
              </Link>
              <Link href="/wiki" className={getLinkClassName('/wiki')} onClick={onClose}>
                Вики
              </Link>
              <Link
                href="/newauthors"
                className={getLinkClassName('/newauthors')}
                onClick={onClose}
              >
                Авторам
              </Link>
              <Link
                href={socialLinks.about}
                target="_blank"
                className="text-white text-mobile-nav font-mobile-nav hover:text-[#898989] transition-colors text-right"
                onClick={onClose}
              >
                О нас
              </Link>
              {isAuthenticated && (
                <Link
                  href="/author-dashboard"
                  className={getLinkClassName('/author-dashboard')}
                  onClick={onClose}
                >
                  Кабинет автора
                </Link>
              )}
            </div>

            <div className="flex flex-col items-end space-y-6">
              <Link
                href={socialLinks.vk}
                target="_blank"
                className="flex items-center space-x-3 text-[#a84dff] hover:text-[#971ce6] hover:opacity-80 transition-all cursor-pointer"
              >
                <span className="text-mobile-social font-mobile-social">ВКонтакте</span>
                <Users className="w-6 h-6" />
              </Link>
              <Link
                href={socialLinks.youtube}
                target="_blank"
                className="flex items-center space-x-3 text-[#a84dff] hover:text-[#971ce6] hover:opacity-80 transition-all cursor-pointer"
              >
                <span className="text-mobile-social font-mobile-social">Youtube</span>
                <MessageCircle className="w-6 h-6" />
              </Link>
              <Link
                href={socialLinks.telegram}
                target="_blank"
                className="flex items-center space-x-3 text-[#a84dff] hover:text-[#971ce6] hover:opacity-80 transition-all cursor-pointer"
              >
                <span className="text-mobile-social font-mobile-social">Telegram</span>
                <Send className="w-6 h-6" />
              </Link>
              <Link
                href={socialLinks.pinterest}
                target="_blank"
                className="flex items-center space-x-3 text-[#a84dff] hover:text-[#971ce6] hover:opacity-80 transition-all cursor-pointer"
              >
                <span className="text-mobile-social font-mobile-social">Pinterest</span>
                <PinterestIcon className="w-6 h-6" />
              </Link>
              {/* {mounted && (
                <button
                  onClick={() => {
                    toggleTheme()
                    onClose()
                  }}
                  className="flex items-center space-x-3 text-[#a84dff] hover:text-[#971ce6] transition-colors cursor-pointer"
                >
                  <span className="text-mobile-social font-mobile-social">
                    {isDark ? 'Светлая тема' : 'Темная тема'}
                  </span>
                  {isDark ? (
                    <Sun className="w-6 h-6" />
                  ) : (
                    <Moon className="w-6 h-6" />
                  )}
                </button>
              )} */}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
